<script setup lang="ts">
import { ref, onBeforeMount, toRefs, onUpdated, watch } from 'vue';
import UserRemoveReferral from './UserRemoveReferral.vue';
import Calendar from 'primevue/calendar';
import InputNumber from 'primevue/inputnumber';
import InputMask from 'primevue/inputmask';
import { EMIT, FREQUENCY_INTERVALS_OPTIONS, FREQUENCY_AMOUNT, REFERRAL_TYPES, PERMISSIONS } from '@/enum';
import { useStudentReferralStore, useServicesStore, useClinicianOrganizationStore } from '@/store';
import useUserTreatmentPlansStore from '@/store/People/Person/TreatmentPlans';
import { can, useDateTime } from '@/composables';
import { ReferralProcessInterface, DisciplineStateInterface } from '@/types';
import { DropdownChangeEvent } from 'primevue/dropdown';
import { OrganizationBuildingInterface } from '@/types/BuildingInterface';

interface Props {
  save: boolean;
  organizationId?: number;
  buildings: OrganizationBuildingInterface[];
  disciplines: DisciplineStateInterface[];
  services: DisciplineStateInterface[];
}

const emit = defineEmits([EMIT.SAVE]);
const props = defineProps<Props>();
const { save, organizationId, services } = toRefs(props);
const removeIndex = ref<number>(0);
const openRemove = ref<boolean>(false);

const referralStore = useStudentReferralStore();
const treatmentPlanStore = useUserTreatmentPlansStore();
const servicesStore = useServicesStore();
const clinicianStore = useClinicianOrganizationStore();

const frequencyIntervals = FREQUENCY_INTERVALS_OPTIONS;
const frequencyAmount = FREQUENCY_AMOUNT;

watch(
  () => referralStore.referralOptionsloading,
  () => {
    if (!referralStore.referralOptionsloading && referralStore.referralTypes) {
      const referralTypeId = referralStore.referralTypes?.find((type) => type.title === REFERRAL_TYPES.REASSIGNMENT).id;

      referralStore.reassignmentReferralTableData.forEach((item) => (item.referral_type.value = referralTypeId));
    }
  },
);

const validRow = (row) => {
  let valid = false;
  row.errors = null;
  const errorMessage = 'All required fields must be filled out.';

  if (row.row_status.completed) {
    return true;
  }

  if (row.id.value === '') {
    row.errors = null;
    return false;
  }

  if (
    organizationId.value &&
    row.referral_type.value !== '' &&
    row.id.value !== '' &&
    row.first_name.value !== '' &&
    row.last_name.value !== '' &&
    row.row_status.disable === false &&
    row.row_status.processing === false
  ) {
    if (row.disciplines.value === '') {
      valid = true;
    } else if (
      row.disciplines.value !== '' &&
      row.service_line.value !== '' &&
      row.service_start.value !== '' &&
      row.service_end.value !== '' &&
      row.interval.value !== '' &&
      row.frequency.value !== ''
    ) {
      valid = true;
    } else {
      row.errors = errorMessage;
    }
  } else {
    row.errors = errorMessage;
  }

  return valid;
};

onUpdated(async () => {
  if (save.value === true) {
    referralStore.reassignmentReferralTableData.forEach(async (row, index) => {
      if (!row.row_status.completed) {
        setTimeout(async () => {
          if (validRow(row)) {
            row.row_status.disable = true;
            row.row_status.processing = true;

            let payload: ReferralProcessInterface = {
              organization_id: organizationId.value as number,
              referral_type_id: row.referral_type.value,
              student_id: row.id.value,
              student_sis_id: row.student_sis_id.value,
              student_first_name: row.first_name.value,
              student_last_name: row.last_name.value,
              student_country: row.student_country.value,
              student_state: row.student_state.value,
              student_city: row.student_city.value,
              zipcode: row.zip.value,
              building_campus: props.buildings.find((b) => b.id === row.location.value)?.name,
              building_id: row.location.value,
              gender: row.gender.value,
              birthdate: row.birthdate.value !== '' ? useDateTime().formatDateForApi(row.birthdate.value) : '',
              grade: row.grade.value,
              phone: row.phone.value,
              parent_sis_id: row.parent_sis_id.value,
              parent_first_name: row.parent_first_name.value,
              parent_last_name: row.parent_last_name.value,
              parent_email: row.parent_email.value,
              referred_discipline_id: row.referred_discipline.value,
              discipline_id: row.disciplines.value,
              service_provider_id: row.service_provider.value,
              service_id: row.service_line.value,
              treatment_plan_type_id: row.treatment_plan_type.value,
              treatment_plan_category_id: row.service_category.value,
              treatment_plan_service_type_id: row.service_type.value,
              treatment_service_start_at:
                row.service_start.value !== '' && row.service_start.value !== null
                  ? useDateTime().formatDateForApi(row.service_start.value)
                  : '',
              treatment_service_end_at:
                row.service_end.value !== '' && row.service_end.value !== null
                  ? useDateTime().formatDateForApi(row.service_end.value)
                  : '',
              treatment_service_duration: row.duration.value,
              treatment_service_frequency: row.frequency.value,
              treatment_service_interval:
                row.interval.value !== '' && row.interval.value !== null ? row.interval.value.toLowerCase() : '',
              integration_provider_type_slug: row.integration_provider_type_slug.value,
            };
            payload = referralStore.parseCustomReferralTypes(payload);

            await referralStore.process(payload, index, REFERRAL_TYPES.REASSIGNMENT);

            handleProcessedRow(row);

            if (referralStore.error) referralStore.error = false;
          }
        }, index * 500);
      }
    });

    scrollBack();
    emit(EMIT.SAVE);
  }
});

const scrollBack = () => {
  const scrollableContainer = document.getElementById('reassignTable');
  const childTable = scrollableContainer?.getElementsByClassName('p-datatable-wrapper');
  if (childTable)
    childTable[0]?.scroll({
      left: 0,
      behavior: 'smooth',
    });
};

const handleProcessedRow = (row) => {
  const errorLength = row.errors?.length;
  row.row_status.disable = !errorLength;
  row.row_status.completed = !errorLength;
  row.row_status.processing = false;

  if (!errorLength) row.errors = null;
};

const init = () => {
  referralStore.reassignmentReferralTableData = [];
  for (let i = 0; i < 3; i++) makeBaseRow(i);
};

const makeBaseRow = (index: number) => {
  const referralType = REFERRAL_TYPES.REASSIGNMENT;

  const referralTypeId = referralStore.getReferralTypeId(referralType);
  const referral = referralStore.newRow(referralType);
  referral.key = index;

  if (referral) {
    referral.referral_type.value = referralTypeId as number;
    referralStore.reassignmentReferralTableData.push(referral);
  }
};

const onUpdateRemove = () => {
  openRemove.value = false;
  referralStore.reassignmentReferralTableData.splice(removeIndex.value, 1);
};

const onRemoveRow = (index: number) => {
  if (referralStore.reassignmentReferralTableData.length > 1) {
    removeIndex.value = index;
    openRemove.value = true;
  }
};

const endDateInvalid = (startDate: Date | string, endDate: Date | string) => {
  let invalid = false;
  if (endDate !== '' && endDate !== null && startDate !== '' && startDate !== null) {
    invalid = endDate.getTime() <= startDate.getTime();
  }
  return invalid;
};

const hasDisciplineSelected = (discipline: string, value: string): boolean => {
  if (discipline !== '' && value === null) {
    return true;
  }

  if (discipline !== '' && value === '') {
    return true;
  }

  return false;
};

const serviceRelateFieldDisabled = (discipline: string, disable: boolean) => discipline === '' || disable;

const checkStartDate = (date: Date | string) => (date !== '' ? date : null);

const onTreatmentPlanTypeSelect = async (e: DropdownChangeEvent) => {
  await treatmentPlanStore.getTreatmentPlanCategories(e.value as number);
};

onBeforeMount(() => init());
</script>

<template>
  <UserRemoveReferral :open="openRemove" @update="onUpdateRemove" @cancel="openRemove = false" />
  <DataTable
    id="reassignTable"
    :value="referralStore.reassignmentReferralTableData"
    class="border-solid border-1 border-100 border-round-md"
    :rows="referralStore.reassignmentReferralTableData.length"
    data-key="key"
    scrollable
    scroll-direction="horizontal"
    loading-icon="logo-spinner"
    responsive-layout="scroll"
    row-group-mode="subheader"
    group-rows-by="key"
  >
    <Column class="text-center" style="max-width: 56px" :frozen="true">
      <template #header>
        <Icon name="add_circle" class="add__row__icon cursor-pointer" @click="makeBaseRow" />
      </template>
      <template #body="{ data, index }">
        <Icon
          v-if="!data.row_status.processing && !data.row_status.completed"
          name="remove_circle"
          class="remove__row__icon cursor-pointer"
          :value="data.id.value"
          :disabled="referralStore.mTSSReferralTableData.length < 2"
          @click="onRemoveRow(index)"
        />
        <Icon v-if="data.row_status.processing" name="cached" class="text-primary spin-cached" />
        <Icon v-if="data.row_status.completed" name="check_circle" class="text-primary" />
      </template>
    </Column>
    <Column field="referral_type" header="Referral Type" header-class="required w-screen" :frozen="true">
      <template #body="{ data }">
        <Dropdown
          v-model="data.referral_type.value"
          type="secondary"
          :options="referralStore.referralTypes"
          option-label="title"
          option-value="id"
          placeholder="Referral Type"
          class="w-full"
          :disabled="data.row_status.disable"
          @change="referralStore.onReferralTypeChange(data, REFERRAL_TYPES.REASSIGNMENT)"
        />
      </template>
    </Column>
    <Column field="id" header="SSID" header-class="required" style="max-width: 150px" :frozen="true">
      <template #body="{ data }">
        <InputText v-model="data.id.value" placeholder="SSID" :disabled="data.row_status.disable" class="w-full" />
      </template>
    </Column>
    <Column field="first_name" header="Student First Name" header-class="required" style="width: 100px" :frozen="true">
      <template #body="{ data }">
        <InputText
          v-model="data.first_name.value"
          placeholder="Student First Name"
          :disabled="data.row_status.disable"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="last_name" header="Student Last Name" header-class="required" style="width: 100px" :frozen="true">
      <template #body="{ data }">
        <InputText
          v-model="data.last_name.value"
          placeholder="Student Last Name"
          :disabled="data.row_status.disable"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="zip" header="Zip Code" style="max-width: 150px">
      <template #body="{ data }">
        <InputText
          v-model="data.zip.value"
          :use-grouping="false"
          placeholder="Zip Code"
          :disabled="data.row_status.disable"
          class="w-full"
        />
      </template>
    </Column>
    <Column v-if="buildings && buildings.length > 0" field="location" header="Building/Campus" style="width: 250px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.location.value"
          :options="buildings"
          option-label="name"
          option-value="id"
          placeholder="Select Building/Campus"
          class="w-full"
          :disabled="data.row_status.disable"
        />
      </template>
    </Column>
    <Column field="treatment_plan_type" header="Treatment Plan Type" style="min-width: 280px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.treatment_plan_type.value"
          type="secondary"
          :options="treatmentPlanStore.treatmentPlanTypes"
          placeholder="Select Treatment Plan Type"
          option-value="id"
          option-label="name"
          class="w-full"
          :disabled="data.row_status.disable"
          @change="onTreatmentPlanTypeSelect"
        />
      </template>
    </Column>
    <Column field="service_category" header="Service Category" style="min-width: 240px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.service_category.value"
          type="secondary"
          :disabled="data.treatment_plan_type.value === '' || data.row_status.disable"
          :options="referralStore.treatmentPlanCategoriesByRowTreatmentPlanType(data.treatment_plan_type.value)"
          option-value="id"
          option-label="name"
          placeholder="Select Service Category"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="referred_discipline" header="Referred Discipline" header-class="w-screen" style="max-width: 250px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.referred_discipline.value"
          type="secondary"
          :options="props.disciplines"
          option-label="name"
          option-value="id"
          placeholder="Select Discipline"
          class="w-full"
          append-to="body"
        />
      </template>
    </Column>
    <Column field="services" header="Service" style="min-width: 240px">
      <template #body="{ data }">
        <span v-if="!organizationId" v-text="'Please select an organization'" />
        <Dropdown
          v-else
          v-model="data.disciplines.value"
          type="secondary"
          :options="services"
          :disabled="data.treatment_plan_type.value === '' || data.row_status.disable"
          option-label="name"
          option-value="id"
          placeholder="Select Service"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="service_line" header="Service Line" style="min-width: 200px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.service_line.value"
          type="secondary"
          :options="servicesStore.services"
          :disabled="serviceRelateFieldDisabled(data.disciplines.value, data.row_status.disable)"
          option-label="name"
          option-value="id"
          placeholder="Select Service Line"
          :class="{
            'w-full': true,
            'p-invalid': hasDisciplineSelected(data.disciplines.value, data.service_line.value),
          }"
        />
      </template>
    </Column>
    <Column field="service_type" header="Service Type" style="min-width: 200px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.service_type.value"
          type="secondary"
          :options="referralStore.serviceTypes"
          :disabled="serviceRelateFieldDisabled(data.disciplines.value, data.row_status.disable)"
          placeholder="Select Service Type"
          option-label="title"
          option-value="id"
          :class="{
            'w-full': true,
            'p-invalid': hasDisciplineSelected(data.disciplines.value, data.service_type.value),
          }"
        />
      </template>
    </Column>
    <Column field="gender" header="Gender">
      <template #body="{ data }">
        <Dropdown
          v-model="data.gender.value"
          type="secondary"
          :options="referralStore.genderOptions"
          :disabled="data.row_status.disable"
          option-label="name"
          option-value="value"
          placeholder="Select Gender"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="birthdate" header="Birth Date" style="min-width: 200px">
      <template #body="{ data }">
        <Calendar
          id="birthdate"
          v-model="data.birthdate.value"
          :disabled="data.row_status.disable"
          placeholder="mm/dd/yyyy"
          date-format="mm/dd/yy"
          :manual-input="true"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="grade" header="Grade">
      <template #body="{ data }">
        <Dropdown
          v-model="data.grade.value"
          type="secondary"
          :options="referralStore.gradeOptions"
          :disabled="data.row_status.disable"
          placeholder="Select Grade"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="phone" header="Phone">
      <template #body="{ data }">
        <InputMask
          id="phone"
          v-model="data.phone.value"
          mask="(999) 999-9999"
          placeholder="(999) 999-9999"
          :disabled="data.row_status.disable"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="parent_first_name" header="Parent First Name">
      <template #body="{ data }">
        <InputText
          v-model="data.parent_first_name.value"
          placeholder="Parent First Name"
          :disabled="data.row_status.disable"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="parent_last_name" header="Parent Last Name">
      <template #body="{ data }">
        <InputText
          v-model="data.parent_last_name.value"
          placeholder="Parent Last Name"
          :disabled="data.row_status.disable"
          class="w-full"
        />
      </template>
    </Column>
    <Column field="parent_email" header="Parent Email">
      <template #body="{ data }">
        <InputText v-model="data.parent_email.value" placeholder="Parent Email" :disabled="data.row_status.disable" />
      </template>
    </Column>
    <Column field="service_start" header="Service Start Date" style="min-width: 180px">
      <template #body="{ data }">
        <Calendar
          id="service_start"
          v-model="data.service_start.value"
          placeholder="mm/dd/yyyy"
          date-format="mm/dd/yy"
          :manual-input="true"
          :disabled="serviceRelateFieldDisabled(data.disciplines.value, data.row_status.disable)"
          :class="{
            'w-full': true,
            'p-invalid': hasDisciplineSelected(data.disciplines.value, data.service_start.value),
          }"
        />
      </template>
    </Column>
    <Column field="service_end" header="Service End Date" style="min-width: 180px">
      <template #body="{ data }">
        <div class="flex-wrap w-full">
          <Calendar
            id="service_end"
            v-model="data.service_end.value"
            placeholder="mm/dd/yyyy"
            date-format="mm/dd/yy"
            :min-date="checkStartDate(data.service_start.value)"
            :manual-input="true"
            :class="{
              'w-full': true,
              'p-invalid':
                endDateInvalid(data.service_start.value, data.service_end.value) ||
                hasDisciplineSelected(data.disciplines.value, data.service_end.value),
            }"
            :disabled="serviceRelateFieldDisabled(data.disciplines.value, data.row_status.disable)"
          />
          <div v-if="endDateInvalid(data.service_start.value, data.service_end.value)" class="text-red-600 text-center">
            <small>End must come after Start</small>
          </div>
        </div>
      </template>
    </Column>
    <Column field="duration" header="Duration">
      <template #body="{ data }">
        <InputNumber
          v-model="data.duration.value"
          placeholder="Duration"
          :disabled="serviceRelateFieldDisabled(data.disciplines.value, data.row_status.disable)"
          :class="{
            'w-full': true,
            'p-invalid': hasDisciplineSelected(data.disciplines.value, data.duration.value),
          }"
        />
      </template>
    </Column>
    <Column field="interval" header="Interval" style="width: 100px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.interval.value"
          type="secondary"
          :options="frequencyIntervals"
          option-value="value"
          option-label="label"
          placeholder="Select Interval"
          :disabled="serviceRelateFieldDisabled(data.disciplines.value, data.row_status.disable)"
          :class="{ 'w-full': true, 'p-invalid': hasDisciplineSelected(data.disciplines.value, data.interval.value) }"
        />
      </template>
    </Column>
    <Column field="frequency" header="Frequency" style="min-width: 180px">
      <template #body="{ data }">
        <Dropdown
          v-model="data.frequency.value"
          type="secondary"
          :options="frequencyAmount"
          option-value="value"
          option-label="label"
          placeholder="Select Frequency"
          :disabled="serviceRelateFieldDisabled(data.disciplines.value, data.row_status.disable)"
          :class="{ 'w-full': true, 'p-invalid': hasDisciplineSelected(data.disciplines.value, data.frequency.value) }"
        />
      </template>
    </Column>
    <Column
      v-if="can([PERMISSIONS.REFERRAL_ASSIGN])"
      field="service_provider"
      header="Service Provider"
      style="min-width: 250px"
    >
      <template #body="{ data }">
        <span
          v-if="!clinicianStore.clinicians || !clinicianStore.clinicians.length"
          v-text="'Please select an organization'"
        />
        <Dropdown
          v-else
          v-model="data.service_provider.value"
          type="secondary"
          :options="clinicianStore.clinicians"
          :disabled="data.row_status.disable"
          option-label="display_name"
          option-value="id"
          placeholder="Select Service Provider"
          class="w-full"
        />
      </template>
    </Column>
    <template #groupfooter="slotProps">
      <div v-if="slotProps.data.errors" class="w-full p-3 border-bottom-1 border-100">
        <Icon name="subdirectory_arrow_right" class="mr-2 text-base text-red-600" />
        <span class="font-bold text-red-600">Errors - {{ slotProps.data.errors }}</span>
      </div>
    </template>
  </DataTable>
</template>

<style lang="scss" scoped>
.add__row__icon {
  color: var(--primary-color);
}

.remove__row__icon {
  color: $red;
}

:deep(th.required) {
  span.p-column-title::after {
    content: '*';
    color: #ff3960;
    margin-left: 0.125rem;
  }
}

.text-red-600 {
  color: $red !important;
}

:deep(.p-frozen-column) {
  margin-right: 0 !important;
  padding-right: 0 !important;
  border-right: none !important;
}

@media (min-width: 768px) {
  :deep(.p-frozen-column) {
    margin-right: 0 !important;
    padding-right: 0 !important;
    border-right: none !important;
  }
}

@media (max-width: 767px) {
  :deep(.p-frozen-column) {
    margin-right: 0 !important;
    padding-right: 0 !important;
    border-right: none !important;
  }
}
</style>
